import { defineStore } from "pinia";
import { ref, triggerRef } from "vue";
import pinia from "./index";
import { TeamGoalApiModel } from "../api-models";

export type GoalSuggestionHistory = {
  suggestions: string[];
  activeSuggestionIndex: number;
};

const useGoalCaptureStore = defineStore("goal-capture-store", () => {
  const goalInputs = ref<{ [x: string]: string }>({});
  const version = ref(0);

  const currentSuggestionText = ref("");
  const goalSuggestions = ref<Record<string, GoalSuggestionHistory>>({});

  const getKey = (index: number): string => {
    return `goal-${index}`;
  };

  const initSuggestionsStore = (goals: TeamGoalApiModel[]): void => {
    goals.forEach((goal, idx) => {
      const key = getKey(idx);
      if (!goalSuggestions.value[key] === undefined) {
        goalSuggestions.value[key] = {
          suggestions: [],
          activeSuggestionIndex: idx,
        };
      }
    });
    triggerRef(goalSuggestions);
  };

  const addGoalSuggestion = (suggestion: string, goalIndex: number): void => {
    const key = getKey(goalIndex);

    if (goalSuggestions.value[key] === undefined) {
      goalSuggestions.value[key] = {
        suggestions: [],
        activeSuggestionIndex: goalIndex,
      };
    }

    const suggestionIndex = goalSuggestions.value[key]?.suggestions.indexOf(suggestion);

    if (suggestionIndex === -1) {
      goalSuggestions.value[key].suggestions.push(suggestion);
      triggerRef(goalSuggestions);
      goalSuggestions.value[key].activeSuggestionIndex =
        goalSuggestions.value[key].suggestions.length - 1;
    } else {
      goalSuggestions.value[key].activeSuggestionIndex = suggestionIndex;
    }

    goalSuggestions.value = { ...goalSuggestions.value };
    currentSuggestionText.value = suggestion;
  };

  const deleteSuggestionsForGoal = (goalIndex: number): void => {
    const key = getKey(goalIndex);
    delete goalSuggestions.value[key];
    triggerRef(goalSuggestions);
  };

  const goToPreviousSuggestion = (goalIndex: number) => {
    const key = getKey(goalIndex);
    const currentIndex = goalSuggestions.value[key].activeSuggestionIndex;
    const isCurrentIndexValid = currentIndex !== undefined && currentIndex > 0;
    const suggestionsForGoal = goalSuggestions.value[key].suggestions;

    if (isCurrentIndexValid && goalSuggestions.value[key].suggestions.length > 0) {
      currentSuggestionText.value = suggestionsForGoal[currentIndex - 1];
      goalSuggestions.value[key].activeSuggestionIndex = currentIndex - 1;
    }
    triggerRef(goalSuggestions);
    return currentSuggestionText.value;
  };

  const goToNextSuggestion = (goalIndex: number) => {
    const key = getKey(goalIndex);
    const currentIndex = goalSuggestions.value[key].activeSuggestionIndex;
    const isCurrentIndexValid = currentIndex !== undefined && currentIndex > -1;
    const suggestionsForGoal = goalSuggestions.value[key].suggestions;

    if (
      suggestionsForGoal.length &&
      isCurrentIndexValid &&
      currentIndex < suggestionsForGoal.length - 1
    ) {
      currentSuggestionText.value = suggestionsForGoal[currentIndex + 1];
      goalSuggestions.value[key].activeSuggestionIndex = currentIndex + 1;
    }
    triggerRef(goalSuggestions);
    return currentSuggestionText.value;
  };

  return {
    initSuggestionsStore,
    goalInputs,
    version,
    goalSuggestions,
    addGoalSuggestion,
    deleteSuggestionsForGoal,
    goToPreviousSuggestion,
    goToNextSuggestion,
  };
});

const goalCaptureStore = useGoalCaptureStore(pinia());

export function getGoalInput(goalNumber: number): string {
  return goalCaptureStore.$state.goalInputs[goalNumber] ?? "";
}

export function saveGoalInput(input: string, goalNumber: number, rerunPrompts: boolean): void {
  if (input === goalCaptureStore.goalInputs[goalNumber]) {
    return;
  }
  goalCaptureStore.$state.goalInputs[goalNumber] = input;
  if (rerunPrompts) {
    goalCaptureStore.$state.version += 1;
  }
}

export default goalCaptureStore;
